import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  Check,
  CloudUpload,
  FileText,
  ImageIcon,
  LayoutGrid,
  Upload,
  X,
  type LucideIcon,
} from "lucide-react";
import type { StudioEmptyExample } from "@/features/footwear-studio/StudioEmptyState";
import {
  getUploadRejection,
  uploadRejectionBodySuffix,
  type StudioUploadRejection,
} from "@/features/footwear-studio/studioUploadAcceptance";
import { cn } from "@/lib/utils";

export const RAIL_UPLOAD_EXAMPLES: StudioEmptyExample[] = [
  { label: "Sketch", src: "/upload-examples/sketch.jpg" },
  { label: "Photo", src: "/upload-examples/photo.jpg" },
  { label: "Vector", src: "/upload-examples/vector.png" },
];

export const UPLOAD_CHECKLIST = [
  "One shoe only",
  "Entire shoe visible",
  "True side profile",
  "Clean or plain background",
] as const;

export {
  STUDIO_UPLOAD_MAX_BYTES,
  getUploadRejection,
  isAcceptedUploadFile,
} from "@/features/footwear-studio/studioUploadAcceptance";

export function GuideExamplePlate({ example }: { example: StudioEmptyExample }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
      <div
        className="aspect-[4/3] w-full overflow-hidden"
        style={{
          borderRadius: 7,
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {example.src ? (
          <img
            src={example.src}
            alt={example.label}
            className="h-full w-full object-contain"
            style={{ display: "block", background: "#fff" }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-white">
            <ImageIcon
              className="shrink-0"
              style={{ width: 17, height: 17, color: "var(--text-faint)" }}
              strokeWidth={1.5}
              aria-hidden
            />
          </div>
        )}
      </div>
      <span
        className="text-center"
        style={{ fontSize: 10.5, color: "var(--text-muted)" }}
      >
        {example.label}
      </span>
    </div>
  );
}

export function QuietActionButton({
  label,
  icon: Icon,
  onClick,
  disabled = false,
  testId,
}: {
  label: string;
  icon: LucideIcon;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      data-testid={testId}
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        disabled
          ? "cursor-not-allowed"
          : "cursor-pointer hover:border-[#38bdf8] hover:bg-[rgba(56,189,248,0.12)]",
      )}
      style={{
        gap: 7,
        fontSize: 13,
        fontWeight: 500,
        color: disabled ? "var(--text-faint)" : "var(--text-primary)",
        background: "transparent",
        border: disabled
          ? "1px solid rgba(255,255,255,0.10)"
          : "1px solid rgba(56,189,248,0.55)",
        borderRadius: 9999,
        padding: "10px 22px",
        transition: "background .15s, border-color .15s",
      }}
    >
      <Icon
        style={{
          width: 15,
          height: 15,
          color: disabled ? "rgba(125,211,252,0.35)" : "#7dd3fc",
        }}
        strokeWidth={2}
      />
      {label}
    </button>
  );
}

type FileUploadHandler = (e: ChangeEvent<HTMLInputElement>) => void;

function ToolDockButton({
  label,
  icon: Icon,
  to,
  disabled = false,
  badge,
}: {
  label: string;
  icon: LucideIcon;
  to?: string;
  disabled?: boolean;
  badge?: string;
}) {
  const inner = (
    <div
      className={cn(
        "group flex min-w-0 flex-1 items-center justify-center gap-2.5 rounded-lg border border-white/[0.09] bg-bgNested px-2.5 py-[17px] transition-[border-color,background-color]",
        disabled
          ? "cursor-not-allowed opacity-60"
          : "cursor-pointer hover:border-[rgba(56,189,248,0.55)] hover:bg-[rgba(56,189,248,0.06)]",
      )}
    >
      <Icon
        className={cn(
          "h-4 w-4 shrink-0 text-textSecondary transition-colors",
          !disabled && "group-hover:text-[#38bdf8]",
        )}
        strokeWidth={1.75}
      />
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="truncate whitespace-nowrap text-[13.5px] font-semibold leading-none text-textPrimary">
          {label}
        </span>
        {badge ? (
          <span className="shrink-0 rounded px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-textMuted border border-white/[0.08]">
            {badge}
          </span>
        ) : null}
      </div>
    </div>
  );

  if (disabled || !to) {
    return <div className="min-w-0 flex-1">{inner}</div>;
  }

  return (
    <Link to={to} className="min-w-0 flex-1">
      {inner}
    </Link>
  );
}

function UploadRejectionBody({
  rejection,
  fontSize,
}: {
  rejection: StudioUploadRejection;
  fontSize: number;
}) {
  return (
    <p
      className="text-center"
      style={{
        fontSize,
        color: "var(--text-secondary)",
        lineHeight: 1.45,
      }}
      data-testid="studio-upload-rejection-body"
    >
      <span
        style={{
          fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
          fontSize: 11,
        }}
      >
        {rejection.fileName}
      </span>
      {uploadRejectionBodySuffix(rejection)}
    </p>
  );
}

function UploadRejectDismiss({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDismiss();
      }}
      aria-label="Dismiss"
      data-testid="studio-upload-rejection-dismiss"
      className="absolute top-2 right-2 flex items-center justify-center text-white/45 hover:text-white/75 transition-colors"
      style={{ width: 24, height: 24 }}
    >
      <X style={{ width: 14, height: 14 }} strokeWidth={2} />
    </button>
  );
}

/** Home left panel: drop target (top) + tool dock (bottom). Matches best-results height. */
export function StudioHomeUploadPanel({
  onFileUpload,
  disabled = false,
  testId = "studio-home-upload-drop-zone",
}: {
  onFileUpload: FileUploadHandler;
  disabled?: boolean;
  testId?: string;
}) {
  const [dragActive, setDragActive] = useState(false);
  const [hoverActive, setHoverActive] = useState(false);
  const [rejection, setRejection] = useState<StudioUploadRejection | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const showError = !!rejection && !dragActive;
  const highlightActive = !showError && (dragActive || hoverActive);

  const handleChooseFile = () => {
    if (!onFileUpload || disabled) return;
    setRejection(null);
    inputRef.current?.click();
  };

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    setRejection(null);
    if (e.target.files?.length && onFileUpload) {
      onFileUpload(e);
    }
    e.target.value = "";
  };

  const handleDroppedFile = useCallback(
    (file: File) => {
      const rejected = getUploadRejection(file);
      if (rejected) {
        setRejection(rejected);
        return;
      }
      setRejection(null);
      const input = inputRef.current;
      if (!input || !onFileUpload) return;
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      handleFileSelected({ target: input } as ChangeEvent<HTMLInputElement>);
    },
    [onFileUpload],
  );

  const handleDropZoneDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setRejection(null);
    setDragActive(true);
  };

  const handleDropZoneDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDropZoneDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const related = event.relatedTarget as Node | null;
    if (related && event.currentTarget.contains(related)) return;
    setDragActive(false);
  };

  const handleDropZoneDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleDroppedFile(file);
  };

  let zoneBorder: string;
  let zoneBackground: string;
  let zoneBoxShadow: string | undefined;
  if (showError) {
    zoneBorder = "1.5px dashed rgba(251,191,36,0.55)";
    zoneBackground = "rgba(251,191,36,0.06)";
    zoneBoxShadow = undefined;
  } else if (highlightActive) {
    zoneBorder = "1.5px dashed rgba(56,189,248,0.55)";
    zoneBackground = "rgba(56,189,248,0.05)";
    zoneBoxShadow = "inset 0 0 0 1px rgba(56,189,248,0.12)";
  } else {
    zoneBorder = "1.5px dashed rgba(255,255,255,0.30)";
    zoneBackground = "var(--bg-nested)";
    zoneBoxShadow = undefined;
  }

  let iconPlate: ReactNode;
  if (showError) {
    iconPlate = (
      <div
        className="flex shrink-0 items-center justify-center"
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: "rgba(251,191,36,0.12)",
        }}
      >
        <AlertTriangle
          style={{
            width: 24,
            height: 24,
            color: "var(--semantic-flagged)",
          }}
          strokeWidth={2}
        />
      </div>
    );
  } else {
    iconPlate = (
      <div
        className="flex shrink-0 items-center justify-center transition-[border-color,background-color]"
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          border: highlightActive
            ? "1px solid rgba(56,189,248,0.45)"
            : "1px solid rgba(56,189,248,0.28)",
          background: highlightActive
            ? "rgba(56,189,248,0.12)"
            : "rgba(56,189,248,0.06)",
        }}
      >
        <CloudUpload
          style={{
            width: 24,
            height: 24,
            color: highlightActive ? "#38bdf8" : "#7dd3fc",
          }}
          strokeWidth={2}
        />
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col self-stretch rounded-xl border border-white/[0.06] bg-bgSurface"
      id="studio-home-upload"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleFileSelected}
        className="hidden"
        aria-hidden
        tabIndex={-1}
      />
      <div className="flex min-h-0 flex-1 flex-col px-4 pt-4 pb-2">
        <div
          className="relative flex min-h-0 flex-1 flex-col items-center justify-center rounded-lg transition-[border-color,background-color,box-shadow]"
          style={{
            border: zoneBorder,
            background: zoneBackground,
            boxShadow: zoneBoxShadow,
            gap: 12,
            padding: "28px 20px",
          }}
          data-testid={testId}
          data-upload-reject={showError ? rejection?.kind : undefined}
          onDragEnter={handleDropZoneDragEnter}
          onDragOver={handleDropZoneDragOver}
          onDragLeave={handleDropZoneDragLeave}
          onDrop={handleDropZoneDrop}
          onMouseEnter={() => setHoverActive(true)}
          onMouseLeave={() => setHoverActive(false)}
        >
          {showError ? (
            <UploadRejectDismiss onDismiss={() => setRejection(null)} />
          ) : null}
          {iconPlate}
          <p
            className="text-center"
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "var(--text-primary)",
              lineHeight: 1.3,
            }}
          >
            {showError ? "Can't use this file" : "Drop your side view here"}
          </p>
          {showError && rejection ? (
            <UploadRejectionBody rejection={rejection} fontSize={12} />
          ) : (
            <p
              className="text-center"
              style={{
                fontSize: 12,
                color: "var(--text-secondary)",
                lineHeight: 1.45,
              }}
            >
              Or browse for a file on your computer.
            </p>
          )}
          <QuietActionButton
            label="Browse files"
            icon={Upload}
            onClick={handleChooseFile}
            disabled={!onFileUpload || disabled}
            testId="studio-rail-upload-btn-upload-new"
          />
          <p
            className="text-center"
            style={{
              fontSize: 11,
              color: "var(--text-secondary)",
            }}
          >
            JPG or PNG · Recommended: 2048 px or larger
          </p>
        </div>
      </div>

      <div
        className="flex shrink-0 gap-1.5 border-t border-white/[0.06] px-2 py-2"
        aria-label="Studio modules"
      >
        <ToolDockButton
          label="Collection Plan"
          icon={LayoutGrid}
          to="/footwear-studio/collection-plan"
        />
        <ToolDockButton
          label="Rule Library"
          icon={BookOpen}
          to="/footwear-studio/rules"
        />
        <ToolDockButton
          label="Artwork"
          icon={FileText}
          disabled
          badge="Soon"
        />
      </div>
    </div>
  );
}

export function StudioUploadDropzone({
  onFileUpload,
  disabled = false,
  testId = "studio-upload-drop-zone",
}: {
  onFileUpload: FileUploadHandler | null;
  disabled?: boolean;
  testId?: string;
}) {
  const [dragActive, setDragActive] = useState(false);
  const [rejection, setRejection] = useState<StudioUploadRejection | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const pickingFileRef = useRef(false);

  const showError = !!rejection && !dragActive;

  const handleChooseFile = () => {
    if (!onFileUpload || disabled) return;
    setRejection(null);
    pickingFileRef.current = true;
    inputRef.current?.click();
    window.setTimeout(() => {
      pickingFileRef.current = false;
    }, 300);
  };

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    setRejection(null);
    if (e.target.files?.length && onFileUpload) {
      onFileUpload(e);
    }
    e.target.value = "";
  };

  const handleDroppedFile = useCallback(
    (file: File) => {
      const rejected = getUploadRejection(file);
      if (rejected) {
        setRejection(rejected);
        return;
      }
      setRejection(null);
      const input = inputRef.current;
      if (!input || !onFileUpload) return;
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      handleFileSelected({ target: input } as ChangeEvent<HTMLInputElement>);
    },
    [onFileUpload],
  );

  const handleDropZoneDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setRejection(null);
    setDragActive(true);
  };

  const handleDropZoneDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDropZoneDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const related = event.relatedTarget as Node | null;
    if (related && event.currentTarget.contains(related)) return;
    setDragActive(false);
  };

  const handleDropZoneDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleDroppedFile(file);
  };

  let zoneBorder: string;
  let zoneBackground: string;
  if (showError) {
    zoneBorder = "1px dashed rgba(251,191,36,0.55)";
    zoneBackground = "rgba(251,191,36,0.06)";
  } else if (dragActive) {
    zoneBorder = "1px solid #38bdf8";
    zoneBackground = "rgba(56,189,248,0.08)";
  } else {
    zoneBorder = "1px dashed rgba(255,255,255,0.30)";
    zoneBackground = "var(--bg-nested)";
  }

  const dropzone = (
    <div
      className="relative flex w-full flex-1 min-w-0 flex-col items-center justify-center"
      style={{
        minHeight: 280,
        border: zoneBorder,
        borderRadius: 12,
        background: zoneBackground,
        gap: 14,
        padding: "34px 24px",
      }}
      data-testid={testId}
      data-upload-reject={showError ? rejection?.kind : undefined}
      onDragEnter={handleDropZoneDragEnter}
      onDragOver={handleDropZoneDragOver}
      onDragLeave={handleDropZoneDragLeave}
      onDrop={handleDropZoneDrop}
    >
      {showError ? (
        <UploadRejectDismiss onDismiss={() => setRejection(null)} />
      ) : null}
      {showError ? (
        <div
          className="flex shrink-0 items-center justify-center"
          style={{
            width: 58,
            height: 58,
            borderRadius: 14,
            background: "rgba(251,191,36,0.12)",
          }}
        >
          <AlertTriangle
            style={{
              width: 26,
              height: 26,
              color: "var(--semantic-flagged)",
            }}
            strokeWidth={2}
          />
        </div>
      ) : (
        <div
          className="flex shrink-0 items-center justify-center"
          style={{
            width: 58,
            height: 58,
            borderRadius: 14,
            border: "1px solid rgba(56,189,248,0.28)",
            background: "rgba(56,189,248,0.06)",
          }}
        >
          <CloudUpload
            style={{ width: 26, height: 26, color: "#7dd3fc" }}
            strokeWidth={2}
          />
        </div>
      )}
      <p
        className="text-center"
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: "var(--text-primary)",
          lineHeight: 1.3,
        }}
      >
        {showError ? "Can't use this file" : "Drop your side view here"}
      </p>
      {showError && rejection ? (
        <UploadRejectionBody rejection={rejection} fontSize={12.5} />
      ) : (
        <p
          className="text-center"
          style={{
            fontSize: 12.5,
            color: "var(--text-secondary)",
            lineHeight: 1.45,
          }}
        >
          Or browse for a file on your computer.
        </p>
      )}
      <QuietActionButton
        label="Browse files"
        icon={Upload}
        onClick={handleChooseFile}
        disabled={!onFileUpload || disabled}
        testId="studio-rail-upload-btn-upload-new"
      />
      <p
        className="text-center"
        style={{
          fontSize: 11,
          color: "var(--text-secondary)",
        }}
      >
        JPG or PNG · Recommended: 2048 px or larger
      </p>
    </div>
  );

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleFileSelected}
        className="hidden"
        aria-hidden
        tabIndex={-1}
      />
      {dropzone}
    </>
  );
}

export function StudioBestResultsPanel({
  framedChecklist = false,
}: {
  /** Home uses 2×2 framed cards; modal uses flat checklist */
  framedChecklist?: boolean;
}) {
  return (
    <div
      className="flex min-w-0 flex-1 flex-col rounded-xl border border-white/[0.06] bg-bgSurface"
      style={{ padding: 14 }}
    >
      <p
        className="uppercase"
        style={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: "var(--text-secondary)",
          marginBottom: 10,
        }}
      >
        For best results
      </p>
      <div className="mb-3 flex gap-2">
        {RAIL_UPLOAD_EXAMPLES.map((example) => (
          <GuideExamplePlate key={example.label} example={example} />
        ))}
      </div>
      {framedChecklist ? (
        <div className="grid grid-cols-2 gap-2">
          {UPLOAD_CHECKLIST.map((item) => (
            <div
              key={item}
              className="flex items-center justify-start gap-2 rounded-lg border border-white/[0.07] bg-transparent px-3 py-2.5"
            >
              <Check
                className="shrink-0"
                style={{ width: 14, height: 14, color: "#34d399" }}
                strokeWidth={2.5}
                aria-hidden
              />
              <span className="text-[12px] leading-snug text-textSecondary">{item}</span>
            </div>
          ))}
        </div>
      ) : (
        <div>
          {UPLOAD_CHECKLIST.map((item, index) => (
            <div
              key={item}
              className="flex items-center gap-2"
              style={{
                padding: "7px 0",
                fontSize: 12.5,
                color: "var(--text-secondary)",
                borderTop:
                  index > 0 ? "1px solid rgba(255,255,255,0.05)" : undefined,
              }}
            >
              <Check
                style={{
                  width: 14,
                  height: 14,
                  color: "#5cc8f5",
                  flexShrink: 0,
                }}
                strokeWidth={2.5}
              />
              <span>{item}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
